/**
 * Representative catalog of the contract pricing formula's components a "Price Components"
 * Product Price Basis can be built from (see `ProductPriceBasisSchema` in `dtos/QualityDto.ts`).
 * Complex, index-based pricing is still design-only in this repo (see docs/COMPLEX_PRICING.md),
 * so there is no live, per-contract component breakdown to pull from yet — this catalog stands in
 * for one. Every component's `value` is fixed here and never user-editable: once a live breakdown
 * exists, these values come from the contract itself, not from whoever is configuring a quality
 * formula. Shared between the UI (rendered as a read-only table) and the API
 * (`resolvePriceComponentsTotal`, below) so neither can drift from the other's idea of what a
 * component is worth.
 */
export interface IPriceComponent {
  group: 'Adjustments' | 'Origin Price' | 'Surcharges';
  hint: string;
  key: string;
  label: string;
  /** Whether `value` adds to or subtracts from the total — e.g. Discounts subtract, Premiums add. */
  sign: -1 | 1;
  value: number;
}

export const PRICE_COMPONENTS: IPriceComponent[] = [
  {
    group: 'Origin Price',
    hint: 'The underlying Origin Price of the contract.',
    key: 'ORIGIN_PRICE',
    label: 'Origin Price',
    sign: 1,
    value: 100,
  },
  {
    group: 'Surcharges',
    hint: 'Freight or location adder baked into the contract price.',
    key: 'FREIGHT',
    label: 'Freight',
    sign: 1,
    value: 10,
  },
  {
    group: 'Surcharges',
    hint: 'Fixed, per-window surcharge (e.g. a carrier fuel surcharge) baked into the contract price.',
    key: 'FUEL_SURCHARGE',
    label: 'Fuel Surcharge',
    sign: 1,
    value: 5,
  },
  {
    group: 'Adjustments',
    hint: 'Negotiated reduction applied to the contract price.',
    key: 'DISCOUNTS',
    label: 'Discounts',
    sign: -1,
    value: 3,
  },
  {
    group: 'Adjustments',
    hint: 'Negotiated addition applied to the contract price.',
    key: 'PREMIUMS',
    label: 'Premiums',
    sign: 1,
    value: 2,
  },
  {
    group: 'Adjustments',
    hint: 'Any other price-adjuster component the contract formula defines.',
    key: 'OTHER_ADJUSTERS',
    label: 'Other Price Adjusters',
    sign: 1,
    value: 0,
  },
];

/** Sums the signed value of the given component keys against `PRICE_COMPONENTS` — an unrecognized key contributes 0. */
export const resolvePriceComponentsTotal = (componentKeys: string[]): number =>
  componentKeys.reduce((total, key) => {
    const component = PRICE_COMPONENTS.find(candidate => candidate.key === key);
    return component ? total + component.value * component.sign : total;
  }, 0);
